import React, { FC } from 'react'
import { styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { Text } from '@moonpig/launchpad-components'
import { DepartmentType } from '../departmentMap'
import { DepartmentTabItemType } from '../types'

type DepartmentTabsProps = {
  selectedDepartment: DepartmentType

  department: DepartmentType
  highlightedIndex: number | null
  index: number
  storeId: string
  setActiveDepartment: (department: DepartmentType) => void
  getItemProps: (props: {
    itemIndex: number
    item: DepartmentTabItemType
  }) => Record<string, unknown>
} & React.HTMLProps<HTMLElement>

const Tab = styled.div<{
  isSelected: boolean
  isHighlighted?: boolean
}>`
  cursor: pointer;
  ${s({
    paddingX: 6,
    paddingY: 4,
    fontSize: 14,
    fontWeight: 400,
    border: '2px solid',
    borderRadius: 5,
    height: '40px',
    minWidth: '55px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  })}

  ${({ isSelected, isHighlighted }) => {
    if (isSelected) {
      return s({
        color: 'colorInteractionSelectedState',
        borderColor: 'colorInteractionSelectedState',
        bgcolor: 'colorBackground03',
      })
    }

    if (isHighlighted) {
      return s({
        color: 'colorBlack70',
        borderColor: 'colorBlack10',
        bgcolor: 'colorBorder04',
      })
    }

    return s({
      color: 'colorInteractionControl',
      borderColor: 'colorBorder03',
      bgcolor: 'colorPrimary03',
    })
  }}

  &[data-active='false']:active {
    ${s({
      borderColor: 'colorBlack20',
      bgcolor: 'colorBlack10',
      color: 'colorBlack80',
    })}
  }

  &:focus-visible {
    text-decoration: none;
    outline: none;
    ${s({ boxShadow: 5 })};

    ${({ isSelected }) =>
      !isSelected &&
      s({
        color: 'colorBlack70',
        borderColor: 'colorBlack10',
        bgcolor: 'colorBorder04',
      })}
  }
`

export const DepartmentTab: FC<DepartmentTabsProps> = ({
  selectedDepartment,
  department,
  highlightedIndex,
  index,
  storeId,
  getItemProps,
  setActiveDepartment,
}) => {
  const { name, label } = department
  const isSelected = name === selectedDepartment.name

  const isHighlighted = index === highlightedIndex

  const isGreetz = storeId === 'nl'

  return (
    <Tab
      tabIndex={0}
      data-active={isSelected ? 'true' : 'false'}
      data-highlighted={isHighlighted ? 'true' : 'false'}
      isSelected={isSelected}
      isHighlighted={isHighlighted}
      {...getItemProps({
        itemIndex: index,
        item: { value: department, itemType: 'department' },
      })}
      aria-pressed={isSelected}
      aria-label={`select ${label.default} department`}
      role="button"
      onKeyDown={e => {
        if (e.key === 'Enter') {
          e.preventDefault()
          setActiveDepartment(department)
        }
      }}
    >
      <Text typography="typeBodyLabel">
        {isGreetz ? label.nl : label.default}
      </Text>
    </Tab>
  )
}
